// Client-side health checks + invocation do edge function health-check
import { supabase } from "@/integrations/supabase/client";

export type HealthStatus = "ok" | "warn" | "error";

export type HealthCheck = {
  name: string;
  status: HealthStatus;
  latency_ms: number;
  detail?: string;
};

export type HealthReport = {
  overall: HealthStatus;
  timestamp: string;
  checks: HealthCheck[];
};

async function timed(name: string, fn: () => Promise<HealthCheck>): Promise<HealthCheck> {
  const t0 = performance.now();
  try {
    const r = await fn();
    return { ...r, latency_ms: Math.round(performance.now() - t0) };
  } catch (e) {
    return {
      name,
      status: "error",
      latency_ms: Math.round(performance.now() - t0),
      detail: e instanceof Error ? e.message : String(e),
    };
  }
}

/** Roda checks que precisam ser feitos do client (sessão, RLS, realtime). */
export async function runClientHealthChecks(): Promise<HealthCheck[]> {
  return Promise.all([
    timed("client_session", async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) return { name: "client_session", status: "error", latency_ms: 0, detail: error.message };
      if (!data.session) return { name: "client_session", status: "warn", latency_ms: 0, detail: "no session" };
      return { name: "client_session", status: "ok", latency_ms: 0, detail: data.session.user.email ?? "ok" };
    }),
    timed("client_rls_agents", async () => {
      const { error } = await supabase.from("agents").select("id").limit(1);
      if (error) return { name: "client_rls_agents", status: "error", latency_ms: 0, detail: error.message };
      return { name: "client_rls_agents", status: "ok", latency_ms: 0, detail: "RLS readable" };
    }),
    timed("client_realtime", async () => {
      return new Promise<HealthCheck>((resolve) => {
        const ch = supabase.channel(`hc_${Date.now()}`);
        const timeout = setTimeout(() => {
          supabase.removeChannel(ch);
          resolve({ name: "client_realtime", status: "warn", latency_ms: 0, detail: "subscribe timeout" });
        }, 4000);
        ch.subscribe((status) => {
          if (status === "SUBSCRIBED") {
            clearTimeout(timeout);
            supabase.removeChannel(ch);
            resolve({ name: "client_realtime", status: "ok", latency_ms: 0, detail: "subscribed" });
          } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
            clearTimeout(timeout);
            supabase.removeChannel(ch);
            resolve({ name: "client_realtime", status: "error", latency_ms: 0, detail: status });
          }
        });
      });
    }),
  ]);
}

/** Chama edge function health-check (server-side). */
export async function runServerHealthChecks(): Promise<HealthReport | { error: string }> {
  try {
    const { data, error } = await supabase.functions.invoke("health-check");
    if (error) return { error: error.message };
    return data as HealthReport;
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

/** Roda tudo e devolve relatório consolidado. */
export async function runFullHealthCheck(): Promise<HealthReport> {
  const [clientChecks, serverReport] = await Promise.all([
    runClientHealthChecks(),
    runServerHealthChecks(),
  ]);

  const serverChecks: HealthCheck[] =
    "checks" in serverReport
      ? serverReport.checks
      : [
          {
            name: "server_unreachable",
            status: "error",
            latency_ms: 0,
            detail: serverReport.error,
          },
        ];

  const all = [...clientChecks, ...serverChecks];
  const overall: HealthStatus = all.some((c) => c.status === "error")
    ? "error"
    : all.some((c) => c.status === "warn")
      ? "warn"
      : "ok";

  return {
    overall,
    timestamp: new Date().toISOString(),
    checks: all,
  };
}
